import { ElementType, MouseEvent as ReactMouseEvent, memo } from 'react';
import { Box, Flex } from '@chakra-ui/react';

import { accent, font, neutral, radius, text } from 'styles/revampTokens';

import {
  BlockNavigationClickOptions,
  BlockNavigationDropdownItem,
} from './BlockNavigationDropdown';
import { AVATAR_TINTS, hashIndex, initialsOf, isModifiedClick } from './dashboardNavDropdownShared';

const DropdownBox = Box as ElementType;
const DropdownFlex = Flex as ElementType;

const ChevronRightIcon = () => (
  <svg width='14' height='14' viewBox='0 0 16 16' fill='none'>
    <path
      d='M6 4l4 4-4 4'
      stroke='currentColor'
      strokeWidth='1.4'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

interface DashboardNavigationPageRowProperties {
  page: BlockNavigationDropdownItem;
  isActive: boolean;
  indented?: boolean;
  onHover: (pageId: string | number) => void;
  onPageClick: (page: BlockNavigationDropdownItem, options?: BlockNavigationClickOptions) => void;
}

const DashboardNavigationPageRow = memo(
  ({
    page,
    isActive,
    indented = false,
    onHover,
    onPageClick,
  }: DashboardNavigationPageRowProperties) => {
    const tint = AVATAR_TINTS[hashIndex(page.title, AVATAR_TINTS.length)];

    return (
      <DropdownFlex
        alignItems='center'
        gap='10px'
        px={indented ? '22px' : '10px'}
        py='6px'
        cursor='pointer'
        borderRadius={radius.control}
        bg={isActive ? accent.subtle : 'transparent'}
        transition='background 0.1s ease'
        _hover={{ bg: isActive ? accent.subtle : neutral.surfaceHover }}
        onMouseEnter={() => onHover(page.id)}
        onClick={(event: ReactMouseEvent) => {
          if (isModifiedClick(event)) return;
          event.stopPropagation();
          onPageClick(page);
        }}
        onContextMenu={(event: ReactMouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          onPageClick(page, { openInNewTab: true });
        }}
      >
        <DropdownFlex
          w='26px'
          h='26px'
          flexShrink={0}
          alignItems='center'
          justifyContent='center'
          borderRadius={radius.control}
          bg={tint.bg}
          color={tint.fg}
          fontFamily={font.body}
          fontSize='10px'
          fontWeight='700'
        >
          {initialsOf(page.title)}
        </DropdownFlex>
        <DropdownBox
          flex='1'
          minW={0}
          color={isActive ? accent.solid : text.primary}
          fontFamily={font.body}
          fontSize='13px'
          fontWeight={isActive ? '600' : '500'}
          noOfLines={1}
        >
          {page.title}
        </DropdownBox>
        <DropdownBox color={isActive ? accent.solid : text.muted} flexShrink={0}>
          <ChevronRightIcon />
        </DropdownBox>
      </DropdownFlex>
    );
  },
);

DashboardNavigationPageRow.displayName = 'DashboardNavigationPageRow';

export default DashboardNavigationPageRow;
